import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { SelectField } from '../../components/ui/SelectField';
import { DAY_LABELS, DEFAULT_BUSINESS_TIMEZONE } from '../../lib/leadBot/businessHours';
import type { BotBusinessHours, BusinessHoursWindow } from '../../types/leadBot';

// Business hours — and ⚠️ THEY ARE NOT COSMETIC. They decide which of the two
// handoff modes applies, and the two produce DIFFERENT WORDS: in hours the bot
// says someone will be in touch shortly; out of hours it offers a callback and
// asks what time suits. The bot cannot hand off to a sleeping salesperson, so
// getting these wrong means promising a member of the public an immediate call
// at 2am.
//
// ⚠️ THE TIMEZONE IS THE CLIENT'S, NOT OURS. The agency runs across AU and NZ,
// and Cloud Run's own clock is UTC — a Perth dealership's "first thing" is not
// a Brisbane afternoon.

/**
 * Deliberately a SHORT FIXED list rather than every IANA zone: these are the
 * zones the agency's clients are actually in, and a searchable list of 400
 * would make the common case worse to serve an edge case that does not exist
 * yet. Adding one is a line here.
 */
const TIMEZONES = [
  { value: 'Australia/Brisbane', label: 'Brisbane (QLD)' },
  { value: 'Australia/Sydney', label: 'Sydney / Melbourne / Canberra / Hobart' },
  { value: 'Australia/Adelaide', label: 'Adelaide (SA)' },
  { value: 'Australia/Darwin', label: 'Darwin (NT)' },
  { value: 'Australia/Perth', label: 'Perth (WA)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZ)' },
];

export function BusinessHoursEditor({
  value,
  onChange,
}: {
  value: BotBusinessHours;
  onChange: (next: BotBusinessHours) => void;
}) {
  const windowFor = (day: number): BusinessHoursWindow | undefined =>
    value.windows.find((w) => w.day === day);

  const setDay = (day: number, next: BusinessHoursWindow | null) => {
    const others = value.windows.filter((w) => w.day !== day);
    const windows = next ? [...others, next] : others;
    windows.sort((a, b) => a.day - b.day);
    onChange({ ...value, windows });
  };

  return (
    <div>
      <p className="text-sm font-medium">Business hours</p>
      <p className="mb-2 text-xs text-muted-foreground">
        Decides what the bot says when it hands over. Inside these hours it says someone will be
        in touch shortly; outside them it offers a callback and asks what time suits — it will
        never promise an immediate call when nobody is there.
      </p>

      <div className="mb-3 max-w-sm">
        <SelectField
          value={value.timezone || DEFAULT_BUSINESS_TIMEZONE}
          onValueChange={(timezone) => onChange({ ...value, timezone })}
          options={TIMEZONES}
          aria-label="Business hours timezone"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          The client's local time, not ours.
        </p>
      </div>

      <div className="space-y-1.5">
        {DAY_LABELS.map((label, day) => {
          const window = windowFor(day);
          const open = Boolean(window);
          return (
            <div key={label} className="flex items-center gap-3">
              <label className="flex w-32 items-center gap-2 text-sm">
                <Checkbox
                  checked={open}
                  onCheckedChange={(checked) =>
                    setDay(
                      day,
                      checked === true
                        ? { day, openTime: window?.openTime ?? '09:00', closeTime: window?.closeTime ?? '17:00' }
                        : null,
                    )
                  }
                  aria-label={`${label} open`}
                />
                {label}
              </label>
              <Input
                type="time"
                className="h-8 w-32"
                value={window?.openTime ?? ''}
                disabled={!open}
                onChange={(e) =>
                  window && setDay(day, { ...window, openTime: e.target.value })
                }
                aria-label={`${label} opening time`}
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="time"
                className="h-8 w-32"
                value={window?.closeTime ?? ''}
                disabled={!open}
                onChange={(e) =>
                  window && setDay(day, { ...window, closeTime: e.target.value })
                }
                aria-label={`${label} closing time`}
              />
            </div>
          );
        })}
      </div>

      {value.windows.length === 0 && (
        // ⚠️ Not an error — a bot with no hours is permanently "out of hours",
        // which is a safe state (it only ever offers a callback). Said plainly
        // so it is a choice rather than something discovered later.
        <p className="mt-2 text-xs text-amber-700">
          No days are open, so every handover will offer a callback rather than saying someone
          is available now.
        </p>
      )}
    </div>
  );
}
